import { computed, inject, Injectable, signal } from '@angular/core';
import { slotKey } from '../sla/sla-target-slots';
import { catchError, finalize, forkJoin, map, of, switchMap, type Observable } from 'rxjs';
import type { InternalNote } from '../../../core/api/generated/models/InternalNote';
import type { SlaPolicyTargets } from '../sla/sla-badge';
import { AuthStore } from '../../../core/auth/auth.store';
import { ToastrService } from '../../../shared/ui';
import type { ChannelKindEnumKey } from '../../../core/api/generated/models/ChannelKind';
import { channelAccountStatusEnum } from '../../../core/api/generated/models/ChannelAccountStatus';
import type { ConversationSession } from '../../../core/api/generated/models/ConversationSession';
import {
  CONVERSATION_SOCKET_EVENTS,
  MESSAGE_DELETED_REASON,
  type ConversationChangedEvent,
  type ConversationEventAppendedPayload,
  type ConversationReplyWindowChangedEvent,
  type MessageDeliveryEvent,
  type MessageNewEvent,
  type MessageUpdatedEvent,
} from '../../../core/realtime/conversation-events';
import type { ConversationEvent } from '../../../core/api/generated/models/ConversationEvent';
import type { ListEventsParams } from './conversations-api.service';
import { SocketClientService } from '../../../core/realtime/socket-client.service';
import { mapConversationToView, formatRelativeTime, previewMessage } from '../mappers/conversation.mapper';
import { mapMessageToThread } from '../mappers/message.mapper';
import type { ChannelSummary, ConversationView, ThreadMessage, ThreadQuotedReply } from '../models/conversation-view.types';
import { ConversationsApiService, type ConversationServerFilters, type SendAttachmentInput, type SendTemplateInput } from './conversations-api.service';
import type { ConversationLabel } from '../../../core/api/generated/models/ConversationLabel';
import type { ProviderMessageTemplate } from '../../../core/api/generated/models/ProviderMessageTemplate';
import { IntegrationTemplatesApiService } from '../../integracoes/data/integration-templates-api.service';

export interface LoadConversationsOptions {
  preserveSelection?: boolean;
  selectedConversationId?: string;
  /** Server-side filter params forwarded to the API. When omitted the store reuses its last-known filters. */
  serverFilters?: ConversationServerFilters;
}

interface MessagePageState {
  cursor: string | null;
  hasMore: boolean;
  loadingOlder: boolean;
}

/** Internal note projected for the notes tab. */
export interface NoteView {
  id: string;
  author: string;
  time: string;
  body: string;
  /** ISO creation timestamp, used to place the note marker on the thread timeline. */
  createdAtIso: string;
}

const EMPTY_MESSAGES: readonly ThreadMessage[] = [];
const EMPTY_TEMPLATES: readonly ProviderMessageTemplate[] = [];
const EMPTY_NOTES: readonly NoteView[] = [];
const EMPTY_EVENTS: readonly ConversationEvent[] = [];
const MESSAGE_PAGE_SIZE = 30;

const HISTORY_PAGE_SIZE = 50;

/** Page slice for a conversation's timeline events. */
export interface HistoryPageState {
  items: readonly ConversationEvent[];
  nextCursor: string | null;
  hasMore: boolean;
  loading: boolean;
  filters: ListEventsParams;
}

const EMPTY_HISTORY: HistoryPageState = {
  items: EMPTY_EVENTS,
  nextCursor: null,
  hasMore: false,
  loading: false,
  filters: {},
};

/**
 * Central state for the Conversas workspace: the inbox list, per-conversation message
 * threads, the active selection, and the loading/error flags. Components stay presentational
 * and read from these signals; realtime (Stage 2) and optimistic sends (Stage 4) extend it.
 */
@Injectable({ providedIn: 'root' })
export class ConversationsStore {
  private readonly api = inject(ConversationsApiService);
  private readonly templatesApi = inject(IntegrationTemplatesApiService);
  private readonly authStore = inject(AuthStore);
  private readonly socket = inject(SocketClientService);
  private readonly toasts = inject(ToastrService);
  private realtimeWired = false;

  readonly connectionStatus = this.socket.connectionStatus;

  private readonly conversationsSignal = signal<readonly ConversationView[]>([]);
  private readonly messagesSignal = signal<Record<string, readonly ThreadMessage[]>>({});
  private readonly selectedIdSignal = signal('');
  private readonly loadingConversationsSignal = signal(false);
  private readonly loadingMessagesSignal = signal(false);
  private readonly sendingSignal = signal(false);
  private readonly errorSignal = signal<string | null>(null);
  private readonly pagesSignal = signal<Record<string, MessagePageState>>({});
  /** Message the composer is currently quoting ("Responder"), cleared once the send is
   *  dispatched (or explicitly by the user). Lives here (not on the composer) so the
   *  thread view — which triggers it — and the composer — which renders and consumes it —
   *  share it without extra @Input/@Output plumbing. */
  private readonly replyTargetSignal = signal<ThreadMessage | null>(null);
  readonly replyTarget = this.replyTargetSignal.asReadonly();
  private readonly notesSignal = signal<Record<string, readonly NoteView[]>>({});
  private readonly historySignal = signal<Record<string, HistoryPageState>>({});
  private readonly slaPolicySignal = signal<SlaPolicyTargets | null>(null);
  private slaPolicyLoaded = false;
  // Channel kind per channelAccountId. Populated once on first inbox load; conversations carry
  // only the id, so the inbox needs this lookup to drive icons by real channel type.
  private readonly channelKindsSignal = signal<ReadonlyMap<string, ChannelKindEnumKey>>(new Map());
  private readonly channelToIntegrationSignal = signal<ReadonlyMap<string, string>>(new Map());
  // Resumo do canal (provedor/nome/número/ativo) por channelAccountId — alimenta a seção
  // "Canal" do drawer de detalhes. Mesma fonte (listAllChannelAccounts) dos kinds.
  private readonly channelSummariesSignal = signal<ReadonlyMap<string, ChannelSummary>>(new Map());
  /** Resumo do canal por channelAccountId (provedor/nome/número/ativo). */
  readonly channelSummaries = this.channelSummariesSignal.asReadonly();
  private readonly templatesByChannelSignal = signal<ReadonlyMap<string, readonly ProviderMessageTemplate[]>>(new Map());
  private readonly templatesLoadedFor = new Set<string>();
  private channelKindsLoaded = false;
  /** Last-used server-side filter params; persisted so realtime reloads keep the active filter. */
  private readonly serverFiltersSignal = signal<ConversationServerFilters>({});
  /**
   * Conversation IDs whose thread cache went stale (a `message:new` arrived while the
   * conversation was not selected, so we didn't fetch the bubble at the time). Cleared
   * when the user opens the conversation and we force-reload its messages.
   */
  private readonly staleThreadsSignal = signal<ReadonlySet<string>>(new Set());
  // Coalescing flag, decoupled from the UI loading signal. Set whenever a list reload is
  // in flight (initial or background) so concurrent triggers don't fan out duplicate
  // requests; the UI loading signal stays off on background reloads to avoid the flash.
  private reloadInFlight = false;
  // Coalesces realtime-triggered inbox reloads: a reload requested while one is in flight runs
  // once when the current one settles, instead of firing an N+1 fan-out per socket event.
  private pendingReload: LoadConversationsOptions | null = null;

  readonly slaPolicy = this.slaPolicySignal.asReadonly();

  readonly conversations = this.conversationsSignal.asReadonly();
  readonly messagesByConversation = this.messagesSignal.asReadonly();
  readonly selectedConversationId = this.selectedIdSignal.asReadonly();
  readonly isLoadingConversations = this.loadingConversationsSignal.asReadonly();
  readonly isLoadingMessages = this.loadingMessagesSignal.asReadonly();
  readonly isSending = this.sendingSignal.asReadonly();
  readonly loadError = this.errorSignal.asReadonly();

  readonly selectedNotes = computed<readonly NoteView[]>(() => this.notesSignal()[this.selectedIdSignal()] ?? EMPTY_NOTES);
  readonly selectedHistory = computed<HistoryPageState>(() => this.historySignal()[this.selectedIdSignal()] ?? EMPTY_HISTORY);

  readonly selectedConversation = computed<ConversationView | null>(
    () => this.conversationsSignal().find((conversation) => conversation.id === this.selectedIdSignal()) ?? null,
  );
  readonly hasSelectedConversation = computed(() => this.selectedConversation() !== null);
  readonly selectedMessages = computed<readonly ThreadMessage[]>(
    () => this.messagesSignal()[this.selectedIdSignal()] ?? EMPTY_MESSAGES,
  );
  readonly hasOlderMessages = computed(() => this.pagesSignal()[this.selectedIdSignal()]?.hasMore ?? false);
  readonly isLoadingOlder = computed(() => this.pagesSignal()[this.selectedIdSignal()]?.loadingOlder ?? false);
  /** True when the selected conversation is in a status the backend allows reopening from. */
  readonly canReopen = computed(() => {
    const status = this.selectedConversation()?.status;
    return status === 'resolved' || status === 'archived' || status === 'channel_unavailable';
  });

  /**
   * True when the selected conversation's provider has closed the reply window.
   * Driven by the backend (`replyWindow.open === false`); falls back to false
   * when the API hasn't delivered the snapshot yet so the composer doesn't lock
   * prematurely.
   */
  readonly replyWindowExpired = computed<boolean>(() => {
    const window = this.selectedConversation()?.replyWindow;
    if (!window) return false;
    return !window.open;
  });

  /** How the closed reply window can be reopened on this channel (or null if it can't). */
  readonly replyWindowReopenWith = computed<'template' | null>(
    () => this.selectedConversation()?.replyWindow?.reopenWith ?? null,
  );

  /** False quando o canal da conversa está desativado — o compositor bloqueia o envio.
   * Default true (ausência do flag não bloqueia). */
  readonly selectedConversationChannelActive = computed<boolean>(
    () => this.selectedConversation()?.channelActive !== false,
  );

  /** Integration id that owns the channel account of the selected conversation; null until
   *  the channelKinds fan-out resolves. Drives lookups like template listing. */
  readonly selectedIntegrationId = computed<string | null>(() => {
    const conversation = this.selectedConversation();
    if (!conversation) return null;
    return this.channelToIntegrationSignal().get(conversation.channelAccountId) ?? null;
  });

  /** Approved templates for the selected conversation's channel account, lazy-loaded on
   *  demand via `loadTemplatesForSelectedChannel`. Empty until the load completes. */
  readonly templatesForSelectedChannel = computed<readonly ProviderMessageTemplate[]>(() => {
    const conversation = this.selectedConversation();
    if (!conversation) return EMPTY_TEMPLATES;
    return this.templatesByChannelSignal().get(conversation.channelAccountId) ?? EMPTY_TEMPLATES;
  });

  loadConversations(options: LoadConversationsOptions = {}): void {
    if (this.reloadInFlight) {
      // A load is already running; remember the latest request and run it once on completion.
      this.pendingReload = options;
      return;
    }

    // Persist new server filters; fall back to previously stored ones for realtime reloads.
    if (options.serverFilters !== undefined) {
      this.serverFiltersSignal.set(options.serverFilters);
    }
    const serverFilters = this.serverFiltersSignal();

    this.loadSlaPolicy();
    this.loadChannelKinds();
    const currentMembershipId = this.authStore.session()?.membershipId ?? null;

    // Only show the inbox skeleton on the FIRST load (empty list). Background reloads
    // (realtime message events, post-action reloads like Atribuir/Resolver) keep the
    // existing list rendered so the row content updates silently in-place instead of
    // disappearing into a loading state and flashing back on.
    const isInitialLoad = this.conversationsSignal().length === 0;
    this.reloadInFlight = true;
    if (isInitialLoad) {
      this.loadingConversationsSignal.set(true);
    }
    this.errorSignal.set(null);

    this.api
      .listConversations(serverFilters)
      .pipe(
        switchMap((response) => {
          if (response.data.length === 0) {
            return of([] as ConversationView[]);
          }

          const ids = response.data.map((conversation) => conversation.id);
          // Bulk-fetch labels for every visible conversation in one round-trip so the inbox
          // chip strip is populated without per-row HTTP calls.
          const labelsByConversation$ = this.api
            .lookupAssignedLabels(ids, { silent: true })
            .pipe(
              map((lookup) => lookup.data),
              catchError(() => of({} as Record<string, ConversationLabel[]>)),
            );

          return forkJoin({
            labels: labelsByConversation$,
            views: forkJoin(
              response.data.map((conversation) =>
                forkJoin({
                  contact: this.api.getContact(conversation.contactId, { silent: true }).pipe(catchError(() => of(null))),
                  sessions: this.api
                    .listSessions(conversation.id, { silent: true })
                    .pipe(
                      map((sessionsResponse) => sessionsResponse.data),
                      catchError(() => of([] as ConversationSession[])),
                    ),
                  lastMessage: this.api
                    .listMessages(conversation.id, { sort: 'sent_at_desc', limit: 1 }, { silent: true })
                    .pipe(
                      map((messagesResponse) => messagesResponse.data[0] ?? null),
                      catchError(() => of(null)),
                    ),
                }).pipe(
                  map(({ contact, sessions, lastMessage }) => ({ conversation, contact, sessions, lastMessage })),
                ),
              ),
            ),
          }).pipe(
            map(({ labels, views }) =>
              views.map(({ conversation, contact, sessions, lastMessage }) =>
                mapConversationToView({
                  conversation,
                  contact,
                  sessions,
                  currentMembershipId,
                  lastMessage,
                  // Lazy read: by the time this callback fires (after every per-conversation
                  // forkJoin resolves), loadChannelKinds may have populated the map even if
                  // it was empty when loadConversations was first called. A closure-captured
                  // snapshot at the top of the method would freeze an empty map and leave
                  // every conversation with channel='unknown' until the next reload.
                  channelKind: this.channelKindsSignal().get(conversation.channelAccountId) ?? null,
                  labels: (labels[conversation.id] ?? []).map((label) => ({ id: label.id, name: label.name, color: label.color })),
                }),
              ),
            ),
          );
        }),
        finalize(() => {
          this.loadingConversationsSignal.set(false);
          this.reloadInFlight = false;
          const pending = this.pendingReload;
          if (pending) {
            this.pendingReload = null;
            this.loadConversations(pending);
          }
        }),
        catchError(() => {
          this.errorSignal.set('Não foi possível carregar as conversas.');
          return of([] as ConversationView[]);
        }),
      )
      .subscribe((conversations) => {
        this.conversationsSignal.set(conversations);

        const requested =
          options.selectedConversationId ??
          (options.preserveSelection ? this.selectedIdSignal() : conversations[0]?.id ?? '');
        const nextSelection =
          conversations.find((conversation) => conversation.id === requested)?.id ?? conversations[0]?.id ?? '';

        this.selectedIdSignal.set(nextSelection);

        if (nextSelection) {
          this.loadMessages(nextSelection);
          this.loadNotes(nextSelection);
          this.socket.emit('conversation:subscribe', { conversationId: nextSelection });
        }
      });
  }

  selectConversation(conversationId: string): void {
    if (!conversationId) {
      return;
    }

    // Optimistically clear the unread badge before the server round-trip.
    this.conversationsSignal.update((list) => {
      const idx = list.findIndex((c) => c.id === conversationId);
      if (idx === -1) return list;
      const current = list[idx];
      if (current.unread === 0 && !current.manuallyUnread) return list;
      const next = [...list] as ConversationView[];
      next[idx] = { ...current, unread: 0, manuallyUnread: false };
      return next;
    });
    this.api.markAsRead(conversationId).subscribe(); // fire-and-forget

    this.selectedIdSignal.set(conversationId);
    this.loadMessages(conversationId);
    this.loadNotes(conversationId);
    this.socket.emit('conversation:subscribe', { conversationId });
  }

  markAsUnread(conversationId: string): void {
    this.conversationsSignal.update((list) => {
      const idx = list.findIndex((c) => c.id === conversationId);
      if (idx === -1) return list;
      const next = [...list] as ConversationView[];
      next[idx] = { ...next[idx], manuallyUnread: true };
      return next;
    });
    this.api.markAsUnread(conversationId).subscribe();
  }

  markAsReadFromMenu(conversationId: string): void {
    this.conversationsSignal.update((list) => {
      const idx = list.findIndex((c) => c.id === conversationId);
      if (idx === -1) return list;
      const next = [...list] as ConversationView[];
      next[idx] = { ...next[idx], unread: 0, manuallyUnread: false };
      return next;
    });
    this.api.markAsRead(conversationId).subscribe();
  }

  /**
   * Lazy-load the channelAccountId → kind map. Triggers a follow-up reload once the map
   * arrives, so the first inbox render (which may run before kinds resolve) gets refreshed
   * with the real channel types and icons.
   */
  private loadChannelKinds(): void {
    if (this.channelKindsLoaded) {
      return;
    }
    this.channelKindsLoaded = true;
    this.api
      .listAllChannelAccounts()
      .pipe(catchError(() => of([])))
      .subscribe((accounts) => {
        const map = new Map<string, ChannelKindEnumKey>();
        const integrationMap = new Map<string, string>();
        const summaryMap = new Map<string, ChannelSummary>();
        for (const account of accounts) {
          map.set(account.id, account.kind);
          integrationMap.set(account.id, account.integrationId);
          summaryMap.set(account.id, {
            kind: account.kind,
            name: account.label,
            number: account.externalSenderId ?? null,
            active: account.status === channelAccountStatusEnum.active,
          });
        }
        this.channelKindsSignal.set(map);
        this.channelToIntegrationSignal.set(integrationMap);
        this.channelSummariesSignal.set(summaryMap);
        // Backfill icons on any conversations already mapped before this resolved. No-op
        // when the list is still empty; in that case the in-flight loadConversations will
        // read the now-populated map lazily in its per-conversation map callback (see C1).
        this.conversationsSignal.update((conversations) =>
          conversations.map((conversation) => ({
            ...conversation,
            channel: map.get(conversation.channelAccountId) ?? conversation.channel,
          })),
        );
      });
  }

  private loadSlaPolicy(): void {
    if (this.slaPolicyLoaded) {
      return;
    }
    this.slaPolicyLoaded = true;
    this.api
      .listSlaPolicies({ silent: true })
      .pipe(catchError(() => of({ data: [], meta: { hasNextPage: false, limit: 100, nextCursor: null } })))
      .subscribe((response) => {
        const active = response.data.filter((policy) => policy.isActive);
        const chosen = active.find((policy) => policy.isDefault) ?? active[0] ?? null;
        if (!chosen) {
          this.slaPolicySignal.set(null);
          return;
        }
        // As metas viraram linhas de `sla_targets` (uma por métrica × track) — as mesmas que o motor
        // usa para decidir se um turno estourou. Sem isso o selo mediria contra um número que o
        // backend não conhece.
        this.api
          .listSlaTargets(chosen.id, { silent: true })
          .pipe(catchError(() => of({ data: [] })))
          .subscribe((targets) => {
            const bySlot: Record<string, number> = {};
            for (const target of targets.data) bySlot[slotKey(target.metric, target.track)] = target.targetSeconds;
            this.slaPolicySignal.set(Object.keys(bySlot).length > 0 ? bySlot : null);
          });
      });
  }

  loadNotes(conversationId: string): void {
    const currentMembershipId = this.authStore.session()?.membershipId ?? null;
    this.api
      .listInternalNotes(conversationId, { silent: true })
      .pipe(catchError(() => of({ data: [], meta: { hasNextPage: false, limit: 50, nextCursor: null } })))
      .subscribe((response) => {
        const notes = response.data.filter((note) => !note.isDeleted).map((note) => mapNote(note, currentMembershipId));
        this.notesSignal.update((current) => ({ ...current, [conversationId]: notes }));
      });
  }

  createNote(contentText: string): void {
    const conversationId = this.selectedIdSignal();
    const body = contentText.trim();
    if (!conversationId || body.length === 0) {
      return;
    }
    this.api.createNote(conversationId, body).subscribe({
      next: () => this.loadNotes(conversationId),
      error: () => this.errorSignal.set('Não foi possível salvar a nota.'),
    });
  }

  deleteNote(noteId: string): void {
    const conversationId = this.selectedIdSignal();
    if (!conversationId) {
      return;
    }
    this.api.deleteNote(conversationId, noteId).subscribe({
      next: () => this.loadNotes(conversationId),
      error: () => this.errorSignal.set('Não foi possível excluir a nota.'),
    });
  }

  /**
   * Load the first page of timeline events for a conversation, replacing any prior state.
   * Filters are remembered on the slice so subsequent {@link loadMoreHistory} stays consistent.
   */
  loadHistory(conversationId: string, filters: ListEventsParams = {}): void {
    if (!conversationId) return;
    this.historySignal.update((current) => ({
      ...current,
      [conversationId]: { ...EMPTY_HISTORY, filters, loading: true },
    }));
    this.api.listEvents(conversationId, { ...filters, limit: HISTORY_PAGE_SIZE }).subscribe({
      next: (response) => {
        this.historySignal.update((current) => ({
          ...current,
          [conversationId]: {
            items: response.data,
            nextCursor: response.meta.nextCursor ?? null,
            hasMore: Boolean(response.meta.hasNextPage),
            loading: false,
            filters,
          },
        }));
      },
      error: () => {
        this.historySignal.update((current) => ({
          ...current,
          [conversationId]: { ...(current[conversationId] ?? EMPTY_HISTORY), loading: false, filters },
        }));
        this.errorSignal.set('Não foi possível carregar o histórico da conversa.');
      },
    });
  }

  /** Append the next page (cursor) to the existing slice. No-op when no more pages. */
  loadMoreHistory(conversationId: string): void {
    const current = this.historySignal()[conversationId];
    if (!current || !current.hasMore || current.loading || !current.nextCursor) return;
    this.historySignal.update((state) => ({
      ...state,
      [conversationId]: { ...current, loading: true },
    }));
    this.api.listEvents(conversationId, { ...current.filters, cursor: current.nextCursor, limit: HISTORY_PAGE_SIZE }).subscribe({
      next: (response) => {
        this.historySignal.update((state) => {
          const prev = state[conversationId] ?? EMPTY_HISTORY;
          return {
            ...state,
            [conversationId]: {
              items: [...prev.items, ...response.data],
              nextCursor: response.meta.nextCursor ?? null,
              hasMore: Boolean(response.meta.hasNextPage),
              loading: false,
              filters: prev.filters,
            },
          };
        });
      },
      error: () => {
        this.historySignal.update((state) => ({
          ...state,
          [conversationId]: { ...(state[conversationId] ?? EMPTY_HISTORY), loading: false },
        }));
        this.errorSignal.set('Não foi possível carregar mais eventos do histórico.');
      },
    });
  }

  /**
   * Insert a realtime-arrived event into the timeline of the affected conversation, in
   * descending chronological order. Deduplicates by event id so out-of-order or duplicate
   * deliveries do not produce double rows.
   */
  appendHistory(event: ConversationEvent): void {
    this.historySignal.update((state) => {
      const slice = state[event.conversationId];
      if (!slice) return state;
      if (slice.items.some((existing) => existing.id === event.id)) return state;
      const merged = mergeEventDesc(slice.items, event);
      return { ...state, [event.conversationId]: { ...slice, items: merged } };
    });
  }

  /**
   * Remove a message bubble from the open thread when a `message.deleted` event lands. Used
   * both by explicit moderation deletes and by the echo-absorb path: an is_echo of a message
   * we sent via Automato is reconciled into the outbound (which carries the real content) and
   * soft-deleted server-side, so its transient empty "via Instagram" bubble must disappear
   * without waiting for a refetch. Idempotent — no-op if the message isn't in the cache.
   */
  private applyMessageDeletion(payload: ConversationEventAppendedPayload): void {
    if (payload.event_type !== 'message.deleted') return;
    // Only the echo-absorb path removes the bubble live — its transient empty "via Instagram"
    // bubble must vanish. Manual moderation deletes are left to their own timeline treatment so
    // this handler doesn't diverge from how the thread renders a deleted message after a refetch.
    if (readString(payload.payload, 'reason') !== MESSAGE_DELETED_REASON.EchoAbsorbedIntoOutbound) return;
    const messageId = readString(payload.payload, 'messageId');
    if (!messageId) return;
    const conversationId = payload.conversation_id;
    this.messagesSignal.update((current) => {
      const list = current[conversationId];
      if (!list) return current;
      const next = list.filter((message) => message.id !== messageId);
      if (next.length === list.length) return current;
      return { ...current, [conversationId]: next };
    });
  }

  /**
   * Sync `conversation.labels` on the inbox list when a label is assigned or removed
   * (either by this user or by anyone else in the business). Driven by the realtime
   * envelope so the chips update without an inbox reload or browser refresh.
   */
  private applyLabelMutation(payload: ConversationEventAppendedPayload): void {
    if (payload.event_type !== 'conversation.label_added' && payload.event_type !== 'conversation.label_removed') {
      return;
    }
    const labelId = readString(payload.payload, 'labelId');
    if (!labelId) return;
    const conversationId = payload.conversation_id;
    this.conversationsSignal.update((conversations) => {
      const index = conversations.findIndex((conversation) => conversation.id === conversationId);
      if (index === -1) return conversations;
      const current = conversations[index];
      let nextLabels: ConversationView['labels'];
      if (payload.event_type === 'conversation.label_added') {
        if (current.labels.some((label) => label.id === labelId)) return conversations;
        const name = readString(payload.payload, 'labelName') ?? '—';
        const color = readString(payload.payload, 'color') ?? '#94a3b8';
        nextLabels = [...current.labels, { id: labelId, name, color }];
      } else {
        nextLabels = current.labels.filter((label) => label.id !== labelId);
      }
      const next = [...conversations];
      next[index] = { ...current, labels: nextLabels };
      return next;
    });
  }

  /** Connect the realtime socket and wire conversation events into the store (idempotent). */
  connectRealtime(): void {
    if (this.realtimeWired) {
      return;
    }
    this.realtimeWired = true;
    this.socket.connect();

    this.socket.on<MessageNewEvent>(CONVERSATION_SOCKET_EVENTS.messageNew).subscribe((event) => {
      this.onIncomingMessage(event.conversationId, event.messageId);
    });
    this.socket.on<MessageUpdatedEvent>(CONVERSATION_SOCKET_EVENTS.messageUpdated).subscribe((event) => {
      this.onIncomingMessage(event.conversationId, event.messageId);
    });
    this.socket.on<MessageDeliveryEvent>(CONVERSATION_SOCKET_EVENTS.messageDelivery).subscribe((event) => {
      this.onDeliveryEvent(event);
    });
    this.socket.on<ConversationEventAppendedPayload>(CONVERSATION_SOCKET_EVENTS.conversationEvent).subscribe((payload) => {
      this.appendHistory(fromAppendedPayload(payload));
      this.applyLabelMutation(payload);
      this.applyMessageDeletion(payload);
    });
    this.socket
      .on<ConversationReplyWindowChangedEvent>(CONVERSATION_SOCKET_EVENTS.conversationReplyWindowChanged)
      .subscribe((event) => this.applyReplyWindowUpdate(event));
    for (const event of [
      CONVERSATION_SOCKET_EVENTS.conversationNew,
      CONVERSATION_SOCKET_EVENTS.conversationUpdated,
      CONVERSATION_SOCKET_EVENTS.conversationAssigned,
      CONVERSATION_SOCKET_EVENTS.conversationResolved,
      CONVERSATION_SOCKET_EVENTS.conversationArchived,
    ]) {
      this.socket.on<ConversationChangedEvent>(event).subscribe(() => {
        this.loadConversations({ preserveSelection: true });
      });
    }

    this.socket.onReconnect(() => {
      const selectedId = this.selectedIdSignal();
      if (selectedId) {
        this.loadMessages(selectedId, { force: true });
      }
      this.loadConversations({ preserveSelection: true });
    });
  }

  /**
   * Realtime hot-path: instead of reloading the whole thread (which causes a visible flash),
   * fetch just the affected message and append/upsert it in place. The conversation list is
   * still refreshed so the inbox sidebar shows the new lastMessageAt — that update is decoupled
   * from the thread render so it doesn't flicker the bubbles.
   */
  private onIncomingMessage(conversationId: string, messageId?: string): void {
    if (conversationId === this.selectedIdSignal() && messageId) {
      this.fetchAndUpsertMessage(conversationId, messageId);
    } else if (conversationId === this.selectedIdSignal()) {
      // Fallback for legacy payloads without messageId — keep correctness over smoothness.
      this.loadMessages(conversationId, { force: true });
    } else {
      // Conversation isn't open: mark its thread cache as stale so the next select() will
      // force-reload from the server. Without this, the user opens the conversation, sees
      // the cached (now-outdated) bubbles, and only an F5 brings the new message in.
      this.markThreadStale(conversationId);
    }
    // Optimistically bump this conversation to the top of the inbox (WhatsApp pattern).
    // The server-side reload below will reconcile if anything has changed in the meantime.
    this.bumpConversationToTop(conversationId);
    this.loadConversations({ preserveSelection: true });
  }

  private markThreadStale(conversationId: string): void {
    this.staleThreadsSignal.update((current) => {
      if (current.has(conversationId)) return current;
      const next = new Set(current);
      next.add(conversationId);
      return next;
    });
  }

  private clearThreadStale(conversationId: string): void {
    this.staleThreadsSignal.update((current) => {
      if (!current.has(conversationId)) return current;
      const next = new Set(current);
      next.delete(conversationId);
      return next;
    });
  }

  /**
   * Move the conversation to the top of the inbox list immediately, so the user sees the
   * activity reflected before the API reload returns. Idempotent — no-op if already on top
   * or if the conversation isn't currently in the list.
   */
  private bumpConversationToTop(conversationId: string): void {
    this.conversationsSignal.update((list) => {
      const idx = list.findIndex((c) => c.id === conversationId);
      if (idx <= 0) return list; // not found or already first
      const next = [...list];
      const [moved] = next.splice(idx, 1);
      return [moved, ...next];
    });
  }

  private fetchAndUpsertMessage(conversationId: string, messageId: string): void {
    this.api.getMessage(conversationId, messageId).subscribe({
      next: (message) => {
        // A late message:updated for a soft-deleted message (e.g. an echo absorbed into its
        // outbound) must NOT resurrect the bubble: the server still returns deleted messages
        // here, so drop it from the thread instead of upserting it back in.
        if (message.isDeleted) {
          this.messagesSignal.update((current) => {
            const list = current[conversationId];
            if (!list) return current;
            const next = list.filter((m) => m.id !== message.id);
            if (next.length === list.length) return current;
            return { ...current, [conversationId]: next };
          });
          return;
        }
        const thread = mapMessageToThread(message);
        this.messagesSignal.update((current) => {
          const list = current[conversationId] ?? [];
          const existingIndex = list.findIndex((m) => m.id === thread.id);
          if (existingIndex >= 0) {
            const next = [...list];
            next[existingIndex] = { ...list[existingIndex], ...thread };
            return { ...current, [conversationId]: next };
          }
          return { ...current, [conversationId]: [...list, thread] };
        });
      },
      // If the fetch fails (auth, race), fall back to the legacy reload so the user
      // doesn't miss the message — visual flash is preferable to a stale thread.
      error: () => this.loadMessages(conversationId, { force: true }),
    });
  }

  /** Sets the message the composer will quote on next send ("Responder"). */
  setReplyTarget(message: ThreadMessage): void {
    this.replyTargetSignal.set(message);
  }

  clearReplyTarget(): void {
    this.replyTargetSignal.set(null);
  }

  sendMessage(contentText: string): void {
    const conversation = this.selectedConversation();
    const body = contentText.trim();

    if (!conversation || body.length === 0) {
      return;
    }

    const replyTarget = this.replyTargetSignal();
    const tempId = makeTempId();
    this.appendMessage(conversation.id, {
      id: tempId,
      tempId,
      sender: 'agent',
      time: 'agora',
      sentAtIso: new Date().toISOString(),
      body,
      attachments: [],
      deliveryStatus: 'pending',
      sendState: 'sending',
      ...(replyTarget && { replyToMessageId: replyTarget.id, quotedReply: quotedReplyFromTarget(replyTarget) }),
    });
    // Bump the conversation to the top so the inbox reflects "I just sent something here"
    // without waiting for the server-side reload to come back.
    this.bumpConversationToTop(conversation.id);
    this.dispatchSend(conversation.id, body, tempId, replyTarget?.id);
    this.clearReplyTarget();
  }

  retryMessage(messageId: string): void {
    const conversationId = this.selectedIdSignal();
    const message = (this.messagesSignal()[conversationId] ?? []).find((item) => item.id === messageId);
    if (!message || message.sendState !== 'failed') {
      return;
    }
    this.patchMessage(conversationId, messageId, { sendState: 'sending' });
    this.dispatchSend(conversationId, message.body, messageId, message.replyToMessageId);
  }

  /** Patch the local conversation snapshot with the new reply-window state.
   * Idempotent — applied directly to the cached view, no refetch. */
  private applyReplyWindowUpdate(event: ConversationReplyWindowChangedEvent): void {
    this.conversationsSignal.update((list) => {
      const idx = list.findIndex((c) => c.id === event.conversationId);
      if (idx === -1) return list;
      const next = [...list];
      next[idx] = {
        ...list[idx],
        replyWindow: {
          open: event.open,
          expiresAt: event.expiresAt,
          reopenWith: event.reopenWith,
        },
      };
      return next;
    });
  }

  /**
   * Optimistically flip the reply window to "open" right after a template send,
   * before the realtime confirmation arrives. The provider event will reinforce
   * (or correct) this snapshot when it lands.
   */
  optimisticReopenWindow(conversationId: string): void {
    const DAY_MS = 24 * 60 * 60 * 1000;
    this.applyReplyWindowUpdate({
      conversationId,
      open: true,
      expiresAt: new Date(Date.now() + DAY_MS).toISOString(),
      reopenWith: null,
    });
  }

  /** Apply a realtime delivery-status change to the matching outbound message. */
  onDeliveryEvent(event: MessageDeliveryEvent): void {
    if (!event.messageId || !event.deliveryStatus) {
      return;
    }
    this.patchMessage(event.conversationId, event.messageId, {
      deliveryStatus: event.deliveryStatus,
      ...(event.deliveryStatus === 'failed' && { failureReason: event.failureReason ?? null }),
    });
    // Toast ao vivo quando a falha acontece na conversa aberta — o motivo instrutivo aparece na hora,
    // além do tooltip persistente no ⚠. Só na conversa selecionada, para não ser ruído de fundo.
    if (event.deliveryStatus === 'failed' && event.conversationId === this.selectedConversationId()) {
      this.toasts.error(
        event.failureReason?.trim() || 'Não foi possível enviar a mensagem. Toque no aviso para reenviar.',
        'Falha no envio',
      );
    }
  }

  private dispatchSend(conversationId: string, body: string, tempId: string, replyToMessageId?: string): void {
    this.api.sendMessage(conversationId, body, replyToMessageId).subscribe({
      next: (message) => {
        this.reconcile(conversationId, tempId, mapMessageToThread(message));
        this.loadConversations({ preserveSelection: true });
      },
      error: (err) => {
        this.patchMessage(conversationId, tempId, { sendState: 'failed' });
        if (this.isReplyWindowExpiredError(err)) {
          this.markReplyWindowClosed(conversationId, err);
          this.errorSignal.set('A janela de 24h fechou — envie um template aprovado para reabrir.');
          this.replyWindowExpiredSignal.set(conversationId);
        } else {
          this.errorSignal.set('Não foi possível enviar a mensagem.');
        }
      },
    });
  }

  /**
   * Sticky one-shot signal: the conversation whose composer should auto-open
   * the template picker because a send was just rejected with 422. The
   * composer reads-and-clears via `consumeReplyWindowExpired`.
   */
  private readonly replyWindowExpiredSignal = signal<string | null>(null);
  readonly replyWindowExpiredConversationId = this.replyWindowExpiredSignal.asReadonly();
  consumeReplyWindowExpired(): void {
    this.replyWindowExpiredSignal.set(null);
  }

  private isReplyWindowExpiredError(err: unknown): boolean {
    if (!err || typeof err !== 'object') return false;
    const e = err as { status?: number; error?: { error?: string } };
    return e.status === 422 && e.error?.error === 'reply_window_expired';
  }

  private markReplyWindowClosed(conversationId: string, err: unknown): void {
    const details = (err as { error?: { details?: { reopenWith?: 'template' | null; expiresAt?: string | null } } }).error?.details;
    this.applyReplyWindowUpdate({
      conversationId,
      open: false,
      expiresAt: details?.expiresAt ?? null,
      reopenWith: details?.reopenWith ?? null,
    });
  }

  private reconcile(conversationId: string, tempId: string, real: ThreadMessage): void {
    this.messagesSignal.update((current) => {
      const list = current[conversationId] ?? [];
      const withoutDuplicate = list.filter((message) => message.id !== real.id || message.tempId === tempId);
      const next = withoutDuplicate.map((message) => (message.tempId === tempId ? real : message));
      return { ...current, [conversationId]: next };
    });
  }

  private patchMessage(conversationId: string, messageId: string, patch: Partial<ThreadMessage>): void {
    this.messagesSignal.update((current) => {
      const list = current[conversationId];
      if (!list) {
        return current;
      }
      return {
        ...current,
        [conversationId]: list.map((message) => (message.id === messageId ? { ...message, ...patch } : message)),
      };
    });
  }

  sendAttachmentMessage(input: SendAttachmentInput & { previewUrl?: string }): void {
    const conversation = this.selectedConversation();
    if (!conversation) {
      return;
    }

    const replyTarget = this.replyTargetSignal();
    // Optimistic append: render the bubble immediately with the local file metadata so the
    // user gets feedback in <100ms. The realtime `message:new` arrives later and reconciles
    // via `fetchAndUpsertMessage`, swapping the temp bubble for the canonical server view.
    const tempId = makeTempId();
    this.appendMessage(conversation.id, {
      id: tempId,
      tempId,
      sender: 'agent',
      time: 'agora',
      sentAtIso: new Date().toISOString(),
      body: input.caption ?? '',
      attachments: [
        {
          id: tempId,
          fileId: input.fileId,
          contentType: '',
          filename: input.filename ?? null,
          sizeBytes: 0,
        },
      ],
      deliveryStatus: 'pending',
      sendState: 'sending',
      ...(replyTarget && { replyToMessageId: replyTarget.id, quotedReply: quotedReplyFromTarget(replyTarget) }),
    });

    // Same optimistic reorder as text sends — keep the inbox in sync with the thread state.
    this.bumpConversationToTop(conversation.id);
    this.clearReplyTarget();

    this.api.sendAttachment(conversation.id, { ...input, ...(replyTarget && { replyToMessageId: replyTarget.id }) }).subscribe({
      next: (message) => {
        this.reconcile(conversation.id, tempId, mapMessageToThread(message));
        this.loadConversations({ preserveSelection: true });
      },
      error: () => {
        this.patchMessage(conversation.id, tempId, { sendState: 'failed' });
        this.errorSignal.set('Não foi possível enviar o anexo.');
      },
    });
  }

  /** Lazy-loads sendable templates for the channel of the selected conversation. Cached per
   *  channelAccountId so flipping back and forth between conversations doesn't refetch.
   *  Backend filters by `eligibleForSend=true` — picker no longer applies a client-side filter. */
  loadTemplatesForSelectedChannel(options: { force?: boolean } = {}): void {
    const conversation = this.selectedConversation();
    const integrationId = this.selectedIntegrationId();
    if (!conversation || !integrationId) return;
    if (!options.force && this.templatesLoadedFor.has(conversation.channelAccountId)) return;
    this.templatesLoadedFor.add(conversation.channelAccountId);

    // Fire-and-forget freshness check: backend skips if last sync < 5min. Result lands
    // either as a refetch below or via the next call when the user reopens the picker.
    this.templatesApi
      .syncChannel(integrationId, conversation.channelAccountId, { externalAccountId: null }, { ifStale: true })
      .pipe(catchError(() => of(null)))
      .subscribe(() => {
        // No-op: a follow-up listForChannel call (e.g. picker open) will pick up the
        // refreshed snapshot. Keeping this fire-and-forget avoids blocking the UI.
      });

    this.templatesApi
      .listForChannel(integrationId, conversation.channelAccountId, { eligibleForSend: true })
      .pipe(catchError(() => of({ data: [] })))
      .subscribe((response) => {
        this.templatesByChannelSignal.update((current) => {
          const next = new Map(current);
          next.set(conversation.channelAccountId, response.data ?? []);
          return next;
        });
      });
  }

  sendTemplateMessage(input: SendTemplateInput): void {
    const conversation = this.selectedConversation();
    if (!conversation) return;

    const tempId = makeTempId();
    this.appendMessage(conversation.id, {
      id: tempId,
      tempId,
      sender: 'agent',
      time: 'agora',
      sentAtIso: new Date().toISOString(),
      body: input.contentText ?? '',
      attachments: [],
      deliveryStatus: 'pending',
      sendState: 'sending',
    });
    this.bumpConversationToTop(conversation.id);

    // Templates legally reopen a closed window — flip optimistically so the
    // composer banner clears immediately. Server confirmation lands via the
    // `conversation:reply-window-changed` socket event.
    this.optimisticReopenWindow(conversation.id);

    this.api.sendTemplate(conversation.id, input).subscribe({
      next: (message) => {
        this.reconcile(conversation.id, tempId, mapMessageToThread(message));
        this.loadConversations({ preserveSelection: true });
      },
      error: () => {
        this.patchMessage(conversation.id, tempId, { sendState: 'failed' });
        this.errorSignal.set('Não foi possível enviar o template.');
      },
    });
  }

  assignToMe(): void {
    const conversation = this.selectedConversation();
    const membershipId = this.authStore.session()?.membershipId;

    if (!conversation || !membershipId) {
      return;
    }

    this.api.assignToMe(conversation.id, membershipId, conversation.teamId).subscribe({
      next: () => this.loadConversations({ selectedConversationId: conversation.id }),
      error: () => this.errorSignal.set('Não foi possível assumir a conversa.'),
    });
  }

  /** Assign the selected conversation to a specific member (not the current user). */
  assignToMember(membershipId: string): void {
    const conversation = this.selectedConversation();
    if (!conversation || !membershipId) {
      return;
    }
    this.api
      .assign(conversation.id, {
        assigneeType: 'membership',
        assigneeMembershipId: membershipId,
        reason: 'Atribuído pela tela de conversas',
        ...(conversation.teamId ? { teamId: conversation.teamId } : {}),
      })
      .subscribe({
        next: () => this.loadConversations({ selectedConversationId: conversation.id }),
        error: () => this.errorSignal.set('Não foi possível atribuir a conversa.'),
      });
  }

  /** Route the selected conversation to a team without picking an individual assignee. */
  assignToTeam(teamId: string): void {
    const conversation = this.selectedConversation();
    if (!conversation || !teamId) {
      return;
    }
    this.api
      .assign(conversation.id, {
        assigneeType: 'unassigned',
        teamId,
        reason: 'Encaminhado para equipe',
      })
      .subscribe({
        next: () => this.loadConversations({ selectedConversationId: conversation.id }),
        error: () => this.errorSignal.set('Não foi possível encaminhar para a equipe.'),
      });
  }

  assignToAgent(): void {
    const conversation = this.selectedConversation();
    if (!conversation) {
      return;
    }

    this.api.assignToAgent(conversation.id).subscribe({
      next: () => {
        this.loadConversations({ selectedConversationId: conversation.id });
        this.loadMessages(conversation.id, { force: true });
      },
      error: () => this.errorSignal.set('Não foi possível atribuir a conversa ao agente.'),
    });
  }

  /**
   * Clear the current assignee. Uses /assign (not /handoff) because the handoff schema
   * requires a non-null `handoffType` enum value; a plain unassign carries no such intent.
   */
  unassign(): void {
    const conversation = this.selectedConversation();
    if (!conversation) {
      return;
    }
    this.api
      .assign(conversation.id, {
        assigneeType: 'unassigned',
        reason: 'Atribuição removida',
      })
      .subscribe({
        next: () => this.loadConversations({ selectedConversationId: conversation.id }),
        error: () => this.errorSignal.set('Não foi possível remover a atribuição.'),
      });
  }

  resolve(): void {
    const conversation = this.selectedConversation();

    if (!conversation) {
      return;
    }

    this.api.resolve(conversation.id).subscribe({
      next: () => this.loadConversations({ preserveSelection: true }),
      error: () => this.errorSignal.set('Não foi possível resolver a conversa.'),
    });
  }

  reopen(): void {
    const conversation = this.selectedConversation();
    if (!conversation) {
      return;
    }
    this.api.reopen(conversation.id).subscribe({
      next: () => this.loadConversations({ preserveSelection: true }),
      error: (err: { status?: number } | null) => {
        if (err?.status === 409) {
          this.errorSignal.set('Esta conversa já está aberta. Nenhuma ação necessária.');
        } else {
          this.errorSignal.set('Não foi possível reabrir a conversa.');
        }
      },
    });
  }

  archive(): void {
    this.runConversationAction((id) => this.api.archive(id), 'Não foi possível arquivar a conversa.');
  }

  snooze(snoozedUntil: string): void {
    // Both fields must travel together: the backend update handler treats them as
    // independent fields, so sending only snoozedUntil writes the timestamp but leaves
    // status='open' (the UI then shows the conversation as Aberta with no visible
    // snooze state). Forcing status='snoozed' keeps the two in sync.
    this.runConversationAction(
      (id) => this.api.updateConversation(id, { status: 'snoozed', snoozedUntil }),
      'Não foi possível pausar a conversa.',
    );
  }

  /** Resume a paused conversation: clear snoozedUntil and flip status back to open. */
  unsnooze(): void {
    this.runConversationAction(
      (id) => this.api.updateConversation(id, { status: 'open', snoozedUntil: null }),
      'Não foi possível retomar a conversa.',
    );
  }

  changePriority(priority: ConversationView['priority']): void {
    this.runConversationAction((id) => this.api.updateConversation(id, { priority }), 'Não foi possível alterar a prioridade.');
  }

private runConversationAction(action: (id: string) => Observable<unknown>, errorMessage: string): void {
    const conversation = this.selectedConversation();
    if (!conversation) {
      return;
    }
    action(conversation.id).subscribe({
      next: () => this.loadConversations({ preserveSelection: true }),
      error: () => this.errorSignal.set(errorMessage),
    });
  }

  /** Load older messages (one page back) for the selected conversation and prepend them. */
  loadOlderMessages(): void {
    const conversationId = this.selectedIdSignal();
    const page = this.pagesSignal()[conversationId];
    if (!conversationId || !page?.hasMore || page.loadingOlder || !page.cursor) {
      return;
    }

    this.setPage(conversationId, { loadingOlder: true });

    this.api
      .listMessages(
        conversationId,
        { sort: 'sent_at_desc', limit: MESSAGE_PAGE_SIZE, cursor: page.cursor },
        { silent: true },
      )
      .pipe(catchError(() => of({ data: [], meta: { hasNextPage: false, limit: MESSAGE_PAGE_SIZE, nextCursor: null } })))
      .subscribe((response) => {
        const older = response.data
          .map((message) => mapMessageToThread(message))
          .reverse();
        this.messagesSignal.update((current) => ({
          ...current,
          [conversationId]: [...older, ...(current[conversationId] ?? [])],
        }));
        this.setPage(conversationId, {
          cursor: response.meta.nextCursor ?? null,
          hasMore: response.meta.hasNextPage,
          loadingOlder: false,
        });
      });
  }

  private loadMessages(conversationId: string, options: { force?: boolean } = {}): void {
    // Force-reload when the thread cache is stale (a realtime message arrived for this
    // conversation while it wasn't selected) — otherwise we'd show the user the cached
    // version that's missing the newly-arrived bubble.
    const isStale = this.staleThreadsSignal().has(conversationId);
    if (!options.force && !isStale && this.messagesSignal()[conversationId]) {
      return;
    }

    this.loadingMessagesSignal.set(true);
    this.api
      .listMessages(conversationId, { sort: 'sent_at_desc', limit: MESSAGE_PAGE_SIZE })
      .pipe(
        finalize(() => this.loadingMessagesSignal.set(false)),
        catchError(() => {
          this.errorSignal.set('Não foi possível carregar as mensagens da conversa.');
          return of({ data: [], meta: { hasNextPage: false, limit: MESSAGE_PAGE_SIZE, nextCursor: null } });
        }),
      )
      .subscribe((response) => {
        // Server returns newest-first; reverse for chronological display.
        const messages = response.data.map((message) => mapMessageToThread(message)).reverse();
        this.messagesSignal.update((current) => {
          // Preserve still-in-flight optimistic messages the server doesn't know about yet.
          const pending = (current[conversationId] ?? []).filter((message) => message.sendState !== undefined);
          const confirmedIds = new Set(messages.map((message) => message.id));
          const survivingPending = pending.filter((message) => !confirmedIds.has(message.id));
          return { ...current, [conversationId]: [...messages, ...survivingPending] };
        });
        this.setPage(conversationId, {
          cursor: response.meta.nextCursor ?? null,
          hasMore: response.meta.hasNextPage,
          loadingOlder: false,
        });

        const newest = response.data[0];
        if (newest) {
          this.conversationsSignal.update((conversations) =>
            conversations.map((item) =>
              item.id === conversationId
                ? { ...item, lastMessage: previewMessage(newest), lastMessageAt: formatRelativeTime(newest.sentAt) }
                : item,
            ),
          );
        }
        // Cache is now fresh from the server — drop the stale marker so future opens
        // can use the cached version until a new realtime event marks it stale again.
        this.clearThreadStale(conversationId);
      });
  }

  private appendMessage(conversationId: string, message: ThreadMessage): void {
    this.messagesSignal.update((current) => ({
      ...current,
      [conversationId]: [...(current[conversationId] ?? []), message],
    }));
  }

  private setPage(conversationId: string, patch: Partial<MessagePageState>): void {
    this.pagesSignal.update((current) => ({
      ...current,
      [conversationId]: {
        cursor: current[conversationId]?.cursor ?? null,
        hasMore: current[conversationId]?.hasMore ?? false,
        loadingOlder: current[conversationId]?.loadingOlder ?? false,
        ...patch,
      },
    }));
  }
}

function mapNote(note: InternalNote, currentMembershipId: string | null): NoteView {
  return {
    id: note.id,
    author: note.authoredByMembershipId === currentMembershipId ? 'Você' : 'Atendente',
    time: new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }).format(
      new Date(note.createdAt),
    ),
    body: note.contentText,
    createdAtIso: note.createdAt,
  };
}

function makeTempId(): string {
  return `temp-${globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(16).slice(2)}`}`;
}

/**
 * Builds the optimistic bubble's own quoted-reply preview directly from the local reply target —
 * so the quote shows immediately on send, not just after the server confirms it. `reconcile()`
 * swaps this for the server's resolved `quotedReply` once the real message lands.
 */
function quotedReplyFromTarget(target: ThreadMessage): ThreadQuotedReply {
  return {
    unavailable: false,
    messageId: target.id,
    sender: target.sender,
    preview: target.body.trim() || 'Anexo',
  };
}

function readString(payload: Record<string, unknown>, key: string): string | null {
  const value = payload[key];
  return typeof value === 'string' && value.length > 0 ? value : null;
}

/** Insert `event` into a DESC-sorted list of events, preserving order. O(n) — short lists. */
function mergeEventDesc(items: readonly ConversationEvent[], event: ConversationEvent): ConversationEvent[] {
  const idx = items.findIndex((existing) => existing.occurredAt <= event.occurredAt);
  if (idx === -1) return [...items, event];
  return [...items.slice(0, idx), event, ...items.slice(idx)];
}

/** Normalize the snake_case realtime envelope into the generated ConversationEvent shape. */
function fromAppendedPayload(payload: ConversationEventAppendedPayload): ConversationEvent {
  return {
    id: payload.event_id,
    businessId: payload.business_id,
    conversationId: payload.conversation_id,
    sessionId: payload.session_id,
    eventType: payload.event_type as ConversationEvent['eventType'],
    actor: { type: payload.actor_type as ConversationEvent['actor']['type'], id: payload.actor_id },
    occurredAt: payload.occurred_at,
    payload: payload.payload,
  };
}
